import { useEffect, useState } from "react";
import { Box } from "@primer/react";
import { collection, doc, getDocs, query, where } from "firebase/firestore";
import { DashboardLayout } from "../components/DashboardLayout";
import { AiTemplateCreator } from "../components/AiTemplateCreator";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../config/firebase";
import { useWallet } from "../hooks/useWallet";

interface DashboardStats {
  apiKeysCount: number;
  templatesCount: number;
  freeUsage: { used: number; total: number };
  tokens: { used: number; available: number };
}

export default function Dashboard() {
  const { user } = useAuth();
  const { walletData, loading: walletLoading } = useWallet();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    apiKeysCount: 0,
    templatesCount: 0,
    freeUsage: { used: 0, total: 1000 },
    tokens: { used: 0, available: 0 },
  });

  useEffect(() => {
    const loadStats = async () => {
      if (!user) return;
      try {
        const userRef = doc(db, "users", user.uid);
        const [apiKeysSnapshot, templatesSnapshot] = await Promise.all([
          getDocs(query(collection(db, "api_keys"), where("user_ref", "==", userRef))),
          getDocs(query(collection(db, "templates"), where("user_ref", "==", userRef))),
        ]);
        setStats((previous) => ({
          ...previous,
          apiKeysCount: apiKeysSnapshot.size,
          templatesCount: templatesSnapshot.size,
        }));
      } catch (error) {
        console.error("Error loading dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [user]);

  useEffect(() => {
    if (!walletData) return;
    setStats((previous) => ({
      ...previous,
      freeUsage: {
        used: walletData.free_usage,
        total: walletData.free_usage_limit,
      },
      tokens: {
        used: walletData.token_usage,
        available: walletData.available_tokens,
      },
    }));
  }, [walletData]);

  const freeRemaining = Math.max(stats.freeUsage.total - stats.freeUsage.used, 0);
  const statusLoading = loading || walletLoading;

  return (
    <DashboardLayout>
      <Box className="dashboard-home">
        <AiTemplateCreator embedded />

        <Box className="dashboard-status" aria-label="Account status">
          <span>{statusLoading ? "…" : stats.templatesCount} templates</span>
          <i aria-hidden="true" />
          <span>{statusLoading ? "…" : stats.apiKeysCount} API keys</span>
          <i aria-hidden="true" />
          <span>{statusLoading ? "…" : freeRemaining} free generations</span>
          <i aria-hidden="true" />
          <span>{statusLoading ? "…" : stats.tokens.available} tokens</span>
        </Box>
      </Box>
    </DashboardLayout>
  );
}
